import { useEffect, useMemo, useState } from "react";
import { Calendar, Flex, Card, Spin, Popover, DatePicker, Tag } from "antd";

import dayjs from "dayjs";

import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";
import CustomLink from "@/components/ui/CustomLink";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";
import DaySchedule from "@/components/DaySchedule";

import routes from "@/routes";

import { getImportanceOptions } from "@/api/importances";
import { getStatusOptions } from "@/api/statuses";
import { getTaskTypeOptions } from "@/api/taskTypes";

import { ACCESS_LEVELS, STATUS_OPTIONS } from "@/constants/tasks";
import WeekSchedule from "../WeekSchedule";
import TaskCard from "../cards/TaskCard";

const formatLocal = (d) => d.format("YYYY-MM-DD[T]HH:mm:ss");

const dayFormat = "DD.MM.YYYY";

const weekFormat = (value) => {
  if (!value) return "";
  const startOfWeek = value.startOf("week");
  const endOfWeek = value.endOf("week");
  return `${startOfWeek.format(dayFormat)} - ${endOfWeek.format(dayFormat)}`;
};

const getIcon = (color) => {
  return STATUS_OPTIONS.find((item) => item.value === color)?.icon;
};

const MyCalendar = ({ data, unitsTree, isLoading, filters, updateFilters }) => {
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const month = useMemo(() => {
    return filters?.view === "month"
      ? dayjs(filters.startDate).month()
      : dayjs().month();
  }, [filters]);

  const year = useMemo(() => {
    return filters?.view === "month"
      ? dayjs(filters.startDate).year()
      : dayjs().year();
  }, [filters]);

  // const day = useMemo(() => {
  //   return filters?.view !== "month"
  //     ? dayjs(filters.startDate).date()
  //     : dayjs().date();
  // }, [filters]);

  const tasksByDays = useMemo(() => {
    return data.reduce((acc, item) => {
      const day = dayjs(item.due_date || item.scheduled_due_date).date();

      if (!acc[day]) {
        acc[day] = [item];
      } else {
        acc[day].push(item);
      }

      return acc;
    }, {});
  }, [data]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const [statuses, priorities, taskTypes] = await Promise.all([
        getStatusOptions(),
        getImportanceOptions(),
        getTaskTypeOptions(),
      ]);

      setStatuses(statuses.map((item) => ({ ...item, label: item.name })));
      setPriorities(priorities.map((item) => ({ ...item, label: item.name })));
      setTaskTypes(taskTypes.map((item) => ({ ...item, label: item.name })));
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const headerRender = ({ value, onChange }) => {
    const monthOptions = [];
    const yearOptions = [];

    let current = value.clone();

    const localeData = value.localeData();

    // Здесь формируется массив полных названий месяцев для селектора.
    for (let i = 0; i < 12; i++) {
      current = current.month(i);
      monthOptions.push({
        label: localeData.months(current),
        value: i,
      });
    }

    for (let i = year - 10; i < year + 10; i += 1) {
      yearOptions.push({
        label: i,
        value: i,
      });
    }

    return (
      <Flex align="center" justify="space-between" gap={8}>
        <Flex align="center" gap={8}>
          <CustomTreeSelect
            placeholder="Рабочая группа"
            data={unitsTree}
            value={filters.units_uuids}
            onChange={(units_uuids) =>
              updateFilters({ units_uuids: units_uuids })
            }
            style={{
              width: "250px",
            }}
          />
          <CustomSelect
            placeholder="Тип"
            options={ACCESS_LEVELS}
            style={{
              width: 135,
            }}
            popupMatchSelectWidth={false}
            value={filters.type_filter}
            onChange={(val) => updateFilters({ type_filter: val })}
            optionRender={(option) => (
              <Flex gap={5} align="center">
                {option?.data?.icon}
                <span>{option.label}</span>
              </Flex>
            )}
            labelRender={(option) => {
              const item = ACCESS_LEVELS.find(
                (item) => item.value === option.value
              );

              return (
                <Flex gap={5} align="center">
                  {item?.icon}
                  <span>{item.label}</span>
                </Flex>
              );
            }}
          />
          <CustomSelect
            placeholder="Тип задачи"
            options={taskTypes}
            fieldNames={{
              label: "name",
              value: "uuid",
              key: "uuid",
            }}
            style={{
              width: 135,
            }}
            showSearch
            optionFilterProp="name"
            allowClear
            popupMatchSelectWidth={false}
            value={filters.task_type_uuid}
            onChange={(val) => updateFilters({ task_type_uuid: val })}
          />
        </Flex>
        <Flex align="center" gap={8}>
          <CustomSelect
            popupMatchSelectWidth={false}
            placement="bottomRight"
            value={month}
            options={monthOptions}
            onChange={(newMonth) => {
              const now = value.clone().month(newMonth);

              updateFilters({
                startDate: formatLocal(now.startOf("month")),
                endDate: formatLocal(now.endOf("month")),
              });
              onChange(now);
            }}
          />
          <CustomSelect
            popupMatchSelectWidth={false}
            placement="bottomRight"
            value={year}
            options={yearOptions}
            onChange={(newYear) => {
              const now = value.clone().year(newYear);
              updateFilters({
                startDate: formatLocal(now.startOf("month")),
                endDate: formatLocal(now.endOf("month")),
              });
              onChange(now);
            }}
          />
        </Flex>
      </Flex>
    );
  };

  const cellRender = (current) => {
    return dateCellRender(current);
  };

  const dateCellRender = (value) => {
    if (value.month() !== month || isLoading) return null;
    const listData = tasksByDays[value.date()] || [];

    return (
      <>
        {listData.map((item) => (
          <Popover
            arrow={false}
            placement="bottomLeft"
            key={item.uuid}
            content={
              <div
                style={{
                  width: 370,
                  margin: "-12px",
                }}
              >
                <TaskCard
                  task={item}
                  showActions={false}
                  statuses={statuses}
                  priorities={priorities}
                  taskTypes={taskTypes}
                />
              </div>
            }
          >
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <div>
                <Tag
                  color={!item.scheduled_due_date ? item.status?.color : ""}
                  icon={getIcon(
                    !item.scheduled_due_date ? item.status?.color : "gray"
                  )}
                  className="tag-with-icon-color small no-margin"
                />
              </div>
              {!item.scheduled_due_date ? (
                <CustomLink
                  to={routes.taskDetail(item.uuid)}
                  color="var(--color-text-base)"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {`${item?.task_number || item?.template?.template_number}. ${
                    item?.name || item?.template?.name
                  }`}
                </CustomLink>
              ) : (
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {`${item?.task_number || item?.template?.template_number}. ${
                    item?.name || item?.template?.name
                  }`}
                </div>
              )}
            </div>
          </Popover>
        ))}
      </>
    );
  };

  return (
    <Spin spinning={isLoading}>
      <Card>
        {filters.view === "month" ? (
          <Calendar
            cellRender={cellRender}
            mode="month"
            headerRender={headerRender}
            value={dayjs(new Date(year, month, 1))}
          />
        ) : filters.view === "week" ? (
          <Flex vertical gap={12}>
            <DatePicker
              allowClear={false}
              format={weekFormat}
              style={{ width: 200, alignSelf: "flex-end" }}
              value={dayjs(filters.startDate)}
              picker="week"
              showWeek={false}
              onChange={(date, dateString) => {
                if (!date) return;
                updateFilters({
                  startDate: formatLocal(date.startOf("week")),
                  endDate: formatLocal(date.endOf("week")),
                });
              }}
            />
            <WeekSchedule
              start={filters.startDate}
              data={data}
              statuses={statuses}
              priorities={priorities}
              taskTypes={taskTypes}
            />
          </Flex>
        ) : (
          <Flex vertical gap={12}>
            <DigitsDatePicker
              allowClear={false}
              style={{ width: 150, alignSelf: "flex-end" }}
              value={dayjs(filters.startDate)}
              onChange={(date, dateString) => {
                if (!date) return;
                updateFilters({
                  startDate: formatLocal(date.startOf("day")),
                  endDate: formatLocal(date.endOf("day")),
                });
              }}
            />
            <DaySchedule
              day={new Date(filters.startDate)}
              data={data}
              style={{ paddingRight: 3 }}
              statusPlacement="front"
              statuses={statuses}
              priorities={priorities}
              taskTypes={taskTypes}
            />
          </Flex>
        )}
      </Card>
    </Spin>
  );
};

export default MyCalendar;
